import React, { useContext, useEffect } from "react";

import {
  Button,
  CircularProgress,
  Fade,
  Stack,
  Typography,
} from "@mui/material";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { MeetingInfoContext, WindowSizeContext } from "../../../context";

import {
  useLocalVideo,
  useMeetingManager,
  useVideoInputs,
} from "amazon-chime-sdk-component-library-react";

import LocalViewTile from "../../../components/meeting/video/tiles/local-view-tile";
import UploadImageButton from "../components/upload-image-button";

const MobileUploadLayout: React.FC = () => {
  const { isPortrait } = useContext(WindowSizeContext);
  const { connected } = useContext(MeetingInfoContext);
  const [uploadStatus, setUploadStatus] = React.useState<
    "idle" | "uploading" | "success"
  >("idle");
  const [showUploadFeedback, setShowUploadFeedback] = React.useState(false);
  const meetingManager = useMeetingManager();

  const uploadComplete = () => {
    setUploadStatus("success");

    // Keep success state visible briefly, then fade back to preview state.
    setTimeout(() => {
      setShowUploadFeedback(false);
    }, 1200);

    setTimeout(() => {
      setUploadStatus("idle");
    }, 1700);
  };

  const onUploadStart = () => {
    setShowUploadFeedback(true);
    setUploadStatus("uploading");
  };

  const onUploadFailed = () => {
    setShowUploadFeedback(false);
    setUploadStatus("idle");
  };

  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const { devices, selectedDevice } = useVideoInputs();

  const [hasEnabledVideo, setHasEnabledVideo] = React.useState(false);

  const selectedCameraType = React.useMemo<"front" | "back">(() => {
    const selected = devices.find((device) => device.deviceId === selectedDevice);
    const label = (selected?.label || "").toLowerCase();

    if (
      label.includes("front") ||
      label.includes("user") ||
      label.includes("facetime")
    ) {
      return "front";
    }

    if (
      label.includes("back") ||
      label.includes("rear") ||
      label.includes("environment")
    ) {
      return "back";
    }

    return "back";
  }, [devices, selectedDevice]);

  const selectedCameraName =
    selectedCameraType === "front" ? "Front" : "Back";
  const swapCameraName = selectedCameraType === "front" ? "Back" : "Front";

  const getCameraType = (label: string): "front" | "back" | "unknown" => {
    const normalized = label.toLowerCase();

    if (
      normalized.includes("front") ||
      normalized.includes("user") ||
      normalized.includes("facetime")
    ) {
      return "front";
    }

    if (
      normalized.includes("back") ||
      normalized.includes("rear") ||
      normalized.includes("environment")
    ) {
      return "back";
    }

    return "unknown";
  };

  const swapCameraDevice = React.useMemo(() => {
    return devices.find((device) => getCameraType(device.label) === swapCameraName.toLowerCase());
  }, [devices, swapCameraName]);

  const onSwapCamera = async () => {
    if (!swapCameraDevice) {
      return;
    }

    try {
      if (isVideoEnabled) {
        await meetingManager.startVideoInputDevice(swapCameraDevice.deviceId);
      } else {
        meetingManager.selectVideoInputDevice(swapCameraDevice.deviceId);
      }
    } catch (error) {
      console.log("Failed to swap camera device", error);
    }
  };

  useEffect(() => {
    if (hasEnabledVideo) {
      return;
    }

    if (!connected) {
      return;
    }

    setHasEnabledVideo(true);

    if (!isVideoEnabled) {
      toggleVideo();
    }
  }, [connected]);

  return (
    <Stack
      direction={"column"}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: "#19191a",
        alignContent: "center",
        justifyContent: "center",
        justifyItems: "center",
        alignItems: "center",
      }}
    >
      <Stack
        direction={"column"}
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          padding: 1,
        }}
      >
        <LocalViewTile
          showErrorMessage={false}
          viewHeight={100}
          viewWidth={100}
        />
      </Stack>
      {connected && (
        <>
          <Stack
            direction={isPortrait ? "column" : "row"}
            spacing={2}
            alignContent={"center"}
            justifyContent={"center"}
            sx={{
              height: "fit-content",
              width: "fit-content",
              position: "fixed",
              top: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 100,
            }}
          >
            <Typography variant="h5" color={"white"}>
              Image Preview
            </Typography>
          </Stack>
          <Fade in={showUploadFeedback} timeout={{ enter: 200, exit: 600 }}>
            <Stack
              sx={{
                width: {
                  xs: "calc(100vw - 32px)",
                  sm: "420px",
                },
                px: 2,
                py: 1.5,
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 110,
              }}
            >
              {uploadStatus === "success" ? (
                <Stack spacing={2} alignItems="center" justifyContent="center">
                  <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 72 }} />
                  <Typography
                    color="white"
                    fontWeight={700}
                    fontSize={{ xs: "2rem", sm: "2.4rem" }}
                  >
                    Upload Complete
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={2} alignItems="center" justifyContent="center">
                  <CircularProgress
                    size={64}
                    sx={{
                      color: "#38bbcd",
                    }}
                  />
                  <Typography
                    color="white"
                    fontWeight={700}
                    fontSize={{ xs: "2rem", sm: "2.4rem" }}
                  >
                    Uploading...
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Fade>
          <Stack
            direction={"column"}
            spacing={2}
            alignContent={"center"}
            justifyContent={"center"}
            sx={{
              mt: 4,
              padding: 1,
              borderRadius: "10px",
              height: "fit-content",
              width: {
                xs: "calc(100vw - 32px)",
                sm: "420px",
              },
              position: "fixed",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 100,
            }}
          >
            <Stack
              direction={"column"}
              spacing={2}
              alignContent={"center"}
              justifyContent={"center"}
              sx={{
                width: "100%",
              }}
            >
              <UploadImageButton
                reportImageUploaded={uploadComplete}
                onUploadStart={onUploadStart}
                onUploadFailure={onUploadFailed}
                label={`Take with ${selectedCameraName} Camera`}
                fullWidth
              />
              {swapCameraDevice && (
                <Button
                  variant="contained"
                  onClick={onSwapCamera}
                  startIcon={<CameraswitchIcon />}
                  sx={{
                    width: "100%",
                    borderRadius: "1.5rem",
                    px: 2,
                    py: 1,
                    height: "40px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {`Swap to ${swapCameraName} Camera`}
                </Button>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default MobileUploadLayout;
